import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Pagination,
  Paper,
  Stack,
  Typography,
  Box,
  Card,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  MenuItem,
} from "@mui/material";
import { alpha } from "@mui/system";
import { styled } from "@mui/material/styles";
import TableItem from "../../components/TableItem";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory } from "react-router";
import InputField from "../../FormComponents/InputField";
import Alart from "../../components/Alart";
import { getUsersByPaging } from "../../services/User";

// Icons
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PeopleIcon from "@mui/icons-material/People";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius * 1.5,
  },
}));

let _userListState = null;

function Users() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Alert state for disable user action
  const [openDisableWarning, setOpenDisableWarning] = useState(false);
  const [msgDisableWarning, setMsgDisableWarning] = useState({
    title: "Warning",
    content: "Confirm You Need To Disable This User",
  });
  const [disableItemId, setDisableItemId] = useState(null);

  // Table columns configuration
  const columns = [
    { id: "name", label: "Name", minWidth: 120 },
    { id: "phone", label: "Phone", minWidth: 100 },
    { id: "email", label: "Email", minWidth: 120 },
    {
      id: "type",
      label: "Type",
      minWidth: 120,
      format: (value) => (
        <Chip label={value} color="primary" size="small" variant="outlined" />
      ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value}
          color={value === "Locked" ? "error" : "success"}
          size="small"
          variant="outlined"
        />
      ),
    },
    { id: "action", label: "Actions", minWidth: 120 },
  ];

  // Create data row factory function
  function createData(name, phone, email, type, status, action, user_id) {
    return { name, phone, email, type, status, action, user_id };
  }

  // Filter state
  const [filter, setFilter] = useState({
    _fName: "",
    _lName: "",
    _phone: "",
    _email: "",
    _userType: "",
    _isLocked: null,
  });

  // Initial data load
  useEffect(() => {
    if (_userListState) {
      const { page: savedPage, filter: savedFilter } = _userListState;
      _userListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Fetch data from API
  const fetchData = async (userFilter, pageNo) => {
    setLoading(true);
    try {
      // Get users data
      let result = await getUsersByPaging(pageNo, rowsPerPage, userFilter);
      let userSet = result.content;

      // Set total pages and rows for pagination
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      const newSet = [];
      if (userSet && userSet.length > 0) {
        for (let x = 0; x < userSet.length; x++) {
          // Set data in new set list to display in the table
          newSet.push(
            createData(
              userSet[x].user.fname + " " + userSet[x].user.lname,
              userSet[x].user.phone,
              userSet[x].user.email,
              userSet[x].userType,
              userSet[x].user.locked ? "Locked" : "Active",
              userSet[x].user.userId
            )
          );
        }
      }

      // Set rows to table
      setRows(newSet);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle disable user action
  function DisableWarningActionButtons() {
    return (
      <>
        <ActionButton
          variant="contained"
          color="error"
          onClick={async () => {
            // await DiactivateItemById(disableItemId);
            setOpenDisableWarning(false);
            await fetchData(filter, page);
          }}
        >
          Disable
        </ActionButton>
        <ActionButton
          variant="outlined"
          onClick={() => {
            setOpenDisableWarning(false);
          }}
        >
          Cancel
        </ActionButton>
      </>
    );
  }

  const disableUser = async (id) => {
    setDisableItemId(id);
    setOpenDisableWarning(true);
  };

  // Action handler for table actions
  const handleAction = (event, id) => {
    switch (event) {
      case "view":
        _userListState = { page, filter };
        history.push("/template/user_update/" + id);
        break;
      case "edit":
        history.push("/template/user_edit/" + id);
        break;
      case "disable":
        disableUser(id);
        break;
      default:
        return;
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _fName: "",
      _lName: "",
      _phone: "",
      _email: "",
      _userType: "",
      _isLocked: null,
    });
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <>
      <Alart
        open={openDisableWarning}
        message={msgDisableWarning}
        actions={<DisableWarningActionButtons />}
      />
      <StyledPaper elevation={0}>
        {openForm ? (
          // <AddUser setOpenForm={setOpenForm} />
          <></>
        ) : (
          <>
            <HeaderCard>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <PeopleIcon fontSize="large" />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" fontWeight="bold">
                    User Management
                  </Typography>
                  <Typography variant="body2">
                    View and manage system users, permissions and access control
                  </Typography>
                </Grid>
                <Grid item>
                  <ActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenForm(true)}
                  >
                    Add User
                  </ActionButton>
                </Grid>
              </Grid>
            </HeaderCard>

            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <StyledTextField
                placeholder="Search by name or email..."
                variant="outlined"
                size="small"
                sx={{ width: "40%" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  handleFilterChange("_fName", e.target.value);
                  if (
                    e.target.value.length > 2 ||
                    e.target.value.length === 0
                  ) {
                    fetchData({ ...filter, _fName: e.target.value }, page);
                  }
                }}
              />
              <Stack direction="row" spacing={2}>
                <Tooltip title="Toggle Filters">
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={toggleFilters}
                  >
                    Filters
                  </ActionButton>
                </Tooltip>
                <Chip
                  label={`${totalRows} Users`}
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </Box>

            {showFilters && (
              <FilterContainer>
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Advanced Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_fName"
                      value={filter._fName}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="First Name"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_lName"
                      value={filter._lName}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Last Name"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_email"
                      value={filter._email}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Email"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputField
                      name="_phone"
                      value={filter._phone}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Phone"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StyledTextField
                      select
                      label="User Type"
                      name="_userType"
                      fullWidth
                      value={filter._userType}
                      onChange={(e) =>
                        handleFilterChange("_userType", e.target.value)
                      }
                      size="small"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="STAFF_USER">Staff User</MenuItem>
                      <MenuItem value="SALES_PERSON">Sales Person</MenuItem>
                    </StyledTextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StyledTextField
                      select
                      label="User Status"
                      name="_isLocked"
                      fullWidth
                      value={filter._isLocked}
                      onChange={(e) =>
                        handleFilterChange("_isLocked", e.target.value)
                      }
                      size="small"
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value={true}>Locked</MenuItem>
                      <MenuItem value={false}>Active</MenuItem>
                    </StyledTextField>
                  </Grid>
                </Grid>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Tooltip title="Reset Filters">
                      <ActionButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={() => {
                          resetFilters();
                          fetchData({}, page);
                        }}
                      >
                        Reset
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Apply Filters">
                      <ActionButton
                        variant="contained"
                        color="primary"
                        onClick={() => fetchData(filter, page)}
                      >
                        Apply Filters
                      </ActionButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </FilterContainer>
            )}

            <Divider sx={{ my: 2 }} />

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 5 }}>
                <Typography variant="body1" color="textSecondary">
                  No users found. Try adjusting your filters or add a new user.
                </Typography>
              </Box>
            ) : (
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "User view",
                  edit: "User edit",
                }}
                actionRoles={{
                  view: ["ROLE_ADMIN"],
                  edit: ["ROLE_ADMIN"],
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            )}

            {totalPages > 1 && (
              <PaginationContainer>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handleChange}
                  size="medium"
                />
              </PaginationContainer>
            )}
          </>
        )}
      </StyledPaper>
    </>
  );
}

export default Users;
